import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../auth/AuthContext';
import { EMPTY_SRS_COUNTS, ReviewCounts, SrsCountsDto } from './types';
import { fetchSrsCounts, REVIEW_UPDATED_EVENT } from './reviewApi';

const POLL_INTERVAL_MS = 60_000;

export type UseReviewCountsResult = {
  counts: ReviewCounts;
  srsCounts: SrsCountsDto;
  /** True until the first fetch completes while logged in. */
  loading: boolean;
};

const ReviewCountsContext = createContext<UseReviewCountsResult | undefined>(
  undefined,
);

const reviewCountsFromSrs = (srsCounts: SrsCountsDto): ReviewCounts => ({
  all: srsCounts.all,
  courses: srsCounts.courses,
  games: srsCounts.games,
  puzzles: srsCounts.puzzles,
  lines: srsCounts.lines,
  other: srsCounts.other,
});

// Single source of truth for review queue counts so the nav badge, drawer, and
// Review page chips always show the same numbers.
export const ReviewCountsProvider = ({ children }: { children: ReactNode }) => {
  const { authChecked, isLoggedIn } = useAuth();
  const enabled = authChecked && isLoggedIn;
  const [srsCounts, setSrsCounts] = useState<SrsCountsDto>(EMPTY_SRS_COUNTS);
  const [loading, setLoading] = useState(enabled);
  const refreshGeneration = useRef(0);

  const counts = useMemo(() => reviewCountsFromSrs(srsCounts), [srsCounts]);

  useEffect(() => {
    if (!enabled) {
      setSrsCounts(EMPTY_SRS_COUNTS);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const refreshSilently = async () => {
      const generation = ++refreshGeneration.current;
      const next = await fetchSrsCounts();
      if (!cancelled && generation === refreshGeneration.current) {
        setSrsCounts(next);
      }
    };

    void (async () => {
      const generation = ++refreshGeneration.current;
      const next = await fetchSrsCounts();
      if (!cancelled && generation === refreshGeneration.current) {
        setSrsCounts(next);
        setLoading(false);
      }
    })();

    const intervalId = window.setInterval(
      () => void refreshSilently(),
      POLL_INTERVAL_MS,
    );
    const onRefresh = () => void refreshSilently();
    window.addEventListener('focus', onRefresh);
    window.addEventListener(REVIEW_UPDATED_EVENT, onRefresh);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onRefresh);
      window.removeEventListener(REVIEW_UPDATED_EVENT, onRefresh);
    };
  }, [enabled]);

  return (
    <ReviewCountsContext.Provider value={{ counts, srsCounts, loading }}>
      {children}
    </ReviewCountsContext.Provider>
  );
};

export const useReviewCounts = (): UseReviewCountsResult => {
  const context = useContext(ReviewCountsContext);
  if (!context) {
    throw new Error('useReviewCounts must be used within a ReviewCountsProvider');
  }
  return context;
};
